/**
 * Theme Provider Component
 *
 * This file implements the application's theming system with:
 * 1. A ThemeProvider component that wraps the application and provides theme context
 * 2. Global styles that apply theme variables throughout the application
 *
 * The main ThemeProvider component:
 * - Applies theme-based global styles using styled-components
 * - Makes theme data available to all child components
 */

import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';
import { ThemeProvider as StyledThemeProvider, createGlobalStyle } from 'styled-components';

import { showThemeToggle, themeName } from '../config/theme-config';
import { usePersistedState } from '../hooks';
import ThemeToggleButton from '../ui/ThemeToggleButton';
import { max, min } from '../utils/breakpoints';

import { availableThemes } from './themes/AvailableThemes';
import { loopTheme } from './themes/loopTheme';
import type { Theme } from './themes/ThemeTypes';

// TODO: Remove this ANY type once we have a proper type for this
// Type assertions for styled-components
const TypedStyledThemeProvider = StyledThemeProvider as any;

/**
 * Helper method to load the theme by searching through all available themes.
 * @param themeName
 * @returns Theme or undefined if theme name not found.
 */
export function loadTheme(themeName: string): Theme | undefined {
  return availableThemes.find((entry) => entry.name === themeName)?.theme;
}

/**
 * Global styles that apply theme variables
 *
 * This uses styled-components' createGlobalStyle to define CSS that applies
 * throughout the entire application. It creates CSS variables and basic styling
 * based on the current theme.
 */
const GlobalStyle = createGlobalStyle<{ theme: Theme }>`

:root {
  /* CSS Variables for non-styled-component usage */

    /* Color variables */
    --primary: ${(props) => props.theme.colors.primary};
    --secondary: ${(props) => props.theme.colors.secondary};
    --tertiary: ${(props) => props.theme.colors.tertiary};
    --dark: ${(props) => props.theme.colors.dark};
    --light: ${(props) => props.theme.colors.light};
    --extra-light: ${(props) => props.theme.colors.extraLight};
    --white: ${(props) => props.theme.colors.white};
    --black: ${(props) => props.theme.colors.black};
    --grey: ${(props) => props.theme.colors.grey};
    --off: ${(props) => props.theme.colors.off};
    --off-white: ${(props) => props.theme.colors.offWhite};
    --primary-hover: ${(props) => props.theme.colors.primaryHover};
    --error: ${(props) => props.theme.colors.error};
    --success: ${(props) => props.theme.colors.success};
    --success-dark: ${(props) => props.theme.colors.successDark};
    --external: ${(props) => props.theme.colors.external};
    --cheap: ${(props) => props.theme.colors.cheap};
    --day: ${(props) => props.theme.colors.day};
    --peak: ${(props) => props.theme.colors.peak};
    --pale: ${(props) => props.theme.colors.pale};
    --solar: ${(props) => props.theme.colors.solar};
    --solar-light: ${(props) => props.theme.colors.solarLight};
    --footer: ${(props) => props.theme.colors.footer};
    --imported: ${(props) => props.theme.colors.imported};
    --exported: ${(props) => props.theme.colors.exported};
    --progress-bar: ${(props) => props.theme.colors.progressBar};
    --themed-form-headers: ${(props) => props.theme.colors.themedFormHeaders};

    /* Gradient variables */
    --primary-gradient: ${(props) => props.theme.colors.gradients.primary};
    --secondary-gradient: ${(props) => props.theme.colors.gradients.secondary};
    --tertiary-gradient: ${(props) => props.theme.colors.gradients.tertiary};
    --success-gradient: ${(props) => props.theme.colors.gradients.success};
    --danger-gradient: ${(props) => props.theme.colors.gradients.danger};
    --warning-gradient: ${(props) => props.theme.colors.gradients.warning};
    --info-gradient: ${(props) => props.theme.colors.gradients.info};
    --app-gradient: ${(props) => props.theme.colors.gradients.app};
    --electricity-gradient: ${(props) => props.theme.colors.gradients.electricity};
    --heating-gradient: ${(props) => props.theme.colors.gradients.heating};
    --car-gradient: ${(props) => props.theme.colors.gradients.car};
    --solar-gradient: ${(props) => props.theme.colors.gradients.solar};
    --solar-gradient-reverse: ${(props) => props.theme.colors.gradients.solarReverse};
    --header-gradient: ${(props) => props.theme.colors.gradients.header};
    
    /* Vertical gradient variables */
    --primary-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.primary};
    --secondary-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.secondary};
    --tertiary-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.tertiary};
    --success-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.success};
    --danger-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.danger};
    --warning-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.warning};
    --info-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.info};
    --app-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.app};
    --electricity-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.electricity};
    --heating-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.heating};
    --car-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.car};
    --solar-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.solar};
    --solar-gradient-vertical-reverse: ${(props) => props.theme.colors.verticalgradients.solarReverse};
    --header-gradient-vertical: ${(props) => props.theme.colors.verticalgradients.header};
    
    /* Supporting legacy variable names */
    --primary-color: var(--primary);
    --secondary-color: var(--secondary);
    --tertiary-color: var(--tertiary);
    --dark-color: var(--dark);
    --light-color: var(--light);
    --white-color: var(--white);
  }

  /* Color utility classes to match SCSS */
  .color-primary { color: var(--primary); }
  .color-secondary { color: var(--secondary); }
  .color-tertiary { color: var(--tertiary); }
  .color-dark { color: var(--dark); }
  .color-light { color: var(--light); }
  .color-extra-light { color: var(--extra-light); }
  .color-white { color: var(--white); }
  .color-black { color: var(--black); }
  .color-grey { color: var(--grey); }
  .color-off { color: var(--off); }
  .color-off-white { color: var(--off-white); }
  .color-primary-hover { color: var(--primary-hover); }
  .color-error { color: var(--error); }
  .color-success { color: var(--success); }
  .color-success-dark { color: var(--success-dark); }
  .color-external { color: var(--external); }
  .color-cheap { color: var(--cheap); }
  .color-day { color: var(--day); }
  .color-peak { color: var(--peak); }
  .color-pale { color: var(--pale); }
  .color-solar { color: var(--solar); }
  .color-solar-light { color: var(--solar-light); }
  .color-imported { color: var(--imported); }
  .color-exported { color: var(--exported); }
  .color-progress-bar { color: var(--progress-bar); }
  .color-themed-form-headers { color: var(--themed-form-headers); }
  
  .background-color-primary { background-color: var(--primary); }
  .background-color-secondary { background-color: var(--secondary); }
  .background-color-tertiary { background-color: var(--tertiary); }
  .background-color-dark { background-color: var(--dark); }
  .background-color-light { background-color: var(--light); }
  .background-color-extra-light { background-color: var(--extra-light); }
  .background-color-white { background-color: var(--white); }
  .background-color-black { background-color: var(--black); }
  .background-color-grey { background-color: var(--grey); }
  .background-color-off { background-color: var(--off); }
  .background-color-off-white { background-color: var(--off-white); }
  .background-color-primary-hover { background-color: var(--primary-hover); }
  .background-color-error { background-color: var(--error); }
  .background-color-success { background-color: var(--success); }
  .background-color-success-dark { background-color: var(--success-dark); }
  .background-color-external { background-color: var(--external); }
  .background-color-cheap { background-color: var(--cheap); }
  .background-color-day { background-color: var(--day); }
  .background-color-peak { background-color: var(--peak); }
  .background-color-pale { background-color: var(--pale); }
  .background-color-solar { background-color: var(--solar); }
  .background-color-solar-light { background-color: var(--solar-light); }
  .background-color-footer { background-color: var(--footer); }

  .border-color-primary { border-color: var(--primary); }
  .border-color-secondary { border-color: var(--secondary); }
  .border-color-tertiary { border-color: var(--tertiary); }
  .border-color-dark { border-color: var(--dark); }
  .border-color-light { border-color: var(--light); }
  .border-color-extra-light { border-color: var(--extra-light); }
  .border-color-white { border-color: var(--white); }
  .border-color-black { border-color: var(--black); }
  .border-color-grey { border-color: var(--grey); }
  .border-color-off { border-color: var(--off); }
  .border-color-off-white { border-color: var(--off-white); }
  .border-color-primary-hover { border-color: var(--primary-hover); }
  .border-color-error { border-color: var(--error); }
  .border-color-success { border-color: var(--success); }
  .border-color-success-dark { border-color: var(--success-dark); }
  .border-color-external { border-color: var(--external); }
  .border-color-cheap { border-color: var(--cheap); }
  .border-color-day { border-color: var(--day); }
  .border-color-peak { border-color: var(--peak); }
  .border-color-pale { border-color: var(--pale); }
  .border-color-solar { border-color: var(--solar); }
  .border-color-solar-light { border-color: var(--solar-light); }

  .background-primary-gradient { background: var(--primary-gradient); }
  .background-secondary-gradient { background: var(--secondary-gradient); }
  .background-tertiary-gradient { background: var(--tertiary-gradient); }
  .background-success-gradient { background: var(--success-gradient); }
  .background-danger-gradient { background: var(--danger-gradient); }
  .background-warning-gradient { background: var(--warning-gradient); }
  .background-info-gradient { background: var(--info-gradient); }
  .background-app-gradient { background: var(--app-gradient); }
  .background-electricity-gradient { background: var(--electricity-gradient); }
  .background-heating-gradient { background: var(--heating-gradient); }
  .background-car-gradient { background: var(--car-gradient); }
  .background-solar-gradient { background: var(--solar-gradient); }
  .background-solar-gradient-reverse { background: var(--solar-gradient-reverse); }
  .background-header-gradient { background: var(--header-gradient); }
  
  .background-primary-gradient-vertical { background: var(--primary-gradient-vertical); }
  .background-secondary-gradient-vertical { background: var(--secondary-gradient-vertical); }
  .background-tertiary-gradient-vertical { background: var(--tertiary-gradient-vertical); }
  .background-success-gradient-vertical { background: var(--success-gradient-vertical); }
  .background-danger-gradient-vertical { background: var(--danger-gradient-vertical); }
  .background-warning-gradient-vertical { background: var(--warning-gradient-vertical); }
  .background-info-gradient-vertical { background: var(--info-gradient-vertical); }
  .background-app-gradient-vertical { background: var(--app-gradient-vertical); }
  .background-electricity-gradient-vertical { background: var(--electricity-gradient-vertical); }
  .background-heating-gradient-vertical { background: var(--heating-gradient-vertical); }
  .background-car-gradient-vertical { background: var(--car-gradient-vertical); }
  .background-solar-gradient-vertical { background: var(--solar-gradient-vertical); }
  .background-solar-gradient-vertical-reverse { background: var(--solar-gradient-vertical-reverse); }
  .background-header-gradient-vertical { background: var(--header-gradient-vertical); }

  /* Amplify and Bootstrap overrides */
  .amplify-sign-out button {
    border: ${(props) => props.theme.overrides['.amplify-sign-out button'].border};
    background: ${(props) => props.theme.overrides['.amplify-sign-out button'].background};
    font-weight: ${(props) => props.theme.overrides['.amplify-sign-out button'].fontWeight};
    color: ${(props) => props.theme.overrides['.amplify-sign-out button'].color};
    font-size: ${(props) => props.theme.overrides['.amplify-sign-out button'].fontSize};
    padding: ${(props) => props.theme.overrides['.amplify-sign-out button'].padding};
  }

  .custom-switch, .form-switch {
    padding-left: ${(props) => props.theme.overrides['.custom-switch'].paddingLeft};
  }

  /* react-bootstrap v2's Form.Check type="switch" renders .form-switch/.form-check-input (Bootstrap 5
     classes) instead of v1's .custom-switch/.custom-control-input, which this Bootstrap 4 CSS still
     styles. Recreate the track/thumb shape here; StyledSwitch layers brand colours on top.
     The thumb is a background-image (not a ::before pseudo-element) since inputs are replaced
     elements and browsers don't reliably render generated content on them. */
  .form-switch .form-check-input {
    width: 2rem;
    height: 1rem;
    appearance: none;
    background-color: ${(props) => props.theme.colors.light};
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3e%3ccircle cx='16' cy='16' r='12' fill='${(
      props
    ) => encodeURIComponent(props.theme.colors.white)}'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: left center;
    background-size: contain;
    border: 1px solid ${(props) => props.theme.colors.light};
    border-radius: 2rem;
    cursor: pointer;
    transition: background-position 0.15s ease-in-out, background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;

    &:checked {
      background-position: right center;
    }
  }

  /* react-bootstrap v2's CloseButton renders .btn-close (a Bootstrap 5 class); this app still loads
     Bootstrap 4 CSS, which has no rule for it, so provide a base reset + "×" glyph here. Brand-specific
     look (size/colour/background) for modal/alert close buttons is layered on below via .close, .btn-close. */
  .btn-close {
    float: right;
    padding: 0;
    line-height: 1;
    background-color: transparent;
    border: 0;
    cursor: pointer;

    &::after {
      content: '×';
    }
  }

  /* react-bootstrap v2's Accordion.Button renders .accordion-button (a Bootstrap 5 class, with the
     current item's expand affordance as a rotating ::after chevron); this Bootstrap 4 CSS has no rule
     for it, so provide the base behaviour + chevron indicator here. */
  .accordion-button {
    cursor: pointer;
    text-align: left;

    &::after {
      content: '';
      display: inline-block;
      flex-shrink: 0;
      width: 1.25rem;
      height: 1.25rem;
      margin-left: 8px;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='${(
        props
      ) =>
        encodeURIComponent(
          props.theme.colors.dark
        )}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-size: 1.25rem;
      transition: transform 0.2s ease-in-out;
      transform: rotate(180deg);
    }

    &.collapsed::after {
      transform: rotate(0deg);
    }
  }

  ${min.xl} {
    .container, .container-sm, .container-md, .container-lg, .container-xl {
      max-width: ${(props) => props.theme.overrides['${min.xl}']['.container, .container-sm, .container-md, .container-lg, .container-xl'].maxWidth} !important;
    }
  }

  /* Typography */
  body {
    font-family: ${(props) => props.theme.fonts.primaryFont};
    font-weight: ${(props) => props.theme.base.typography.body.fontWeight};
    font-size: ${(props) => props.theme.base.typography.body.fontSize};
    line-height: ${(props) => props.theme.base.typography.body.lineHeight};
  }

  p {
    line-height: ${(props) => props.theme.base.typography.p.lineHeight};
    ${max.sm} {
      font-size: ${(props) => props.theme.base.typography.p['${max.sm}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.p['${max.sm}'].lineHeight};
    }
    b {
      font-weight: ${(props) => props.theme.base.typography.p.b.fontWeight};
    }
  }

  h1 {
    color: ${(props) => props.theme.colors.dark};
    font-weight: ${(props) => props.theme.base.typography.h1.fontWeight};
    font-size: ${(props) => props.theme.base.typography.h1.fontSize};
    line-height: ${(props) => props.theme.base.typography.h1.lineHeight};

    ${max.sm} {
      text-align: ${(props) => props.theme.base.typography.h1['${max.sm}'].textAlign};
    }

    ${min.sm} {
      font-size: ${(props) => props.theme.base.typography.h1['${min.sm}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.h1['${min.sm}'].lineHeight};
    }

    ${min.md} {
      font-size: ${(props) => props.theme.base.typography.h1['${min.md}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.h1['${min.md}'].lineHeight};
    }
  }

  h2 {
    font-weight: ${(props) => props.theme.base.typography.h2.fontWeight};
    color: ${(props) => props.theme.colors.primary};
    font-size: ${(props) => props.theme.base.typography.h2.fontSize};
    line-height: ${(props) => props.theme.base.typography.h2.lineHeight};

    ${min.sm} {
      font-size: ${(props) => props.theme.base.typography.h2['${min.sm}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.h2['${min.sm}'].lineHeight};
    }

    ${min.md} {
      font-size: ${(props) => props.theme.base.typography.h2['${min.md}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.h2['${min.md}'].lineHeight};
    }
  }

  h3 {
    font-weight: ${(props) => props.theme.base.typography.h3.fontWeight};
    color: ${(props) => props.theme.colors.dark};
    font-size: ${(props) => props.theme.base.typography.h3.fontSize};
    line-height: ${(props) => props.theme.base.typography.h3.lineHeight};

    ${min.sm} {
      font-size: ${(props) => props.theme.base.typography.h3['${min.sm}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.h3['${min.sm}'].lineHeight};
    }

    ${min.md} {
      font-size: ${(props) => props.theme.base.typography.h3['${min.md}'].fontSize};
      line-height: ${(props) => props.theme.base.typography.h3['${min.md}'].lineHeight};
    }
  }

  h4 {
    font-weight: ${(props) => props.theme.base.typography.h4.fontWeight};
    color: ${(props) => props.theme.colors.dark};
    font-size: ${(props) => props.theme.base.typography.h4.fontSize};
    line-height: ${(props) => props.theme.base.typography.h4.lineHeight};
  }

  h5 {
    font-size: ${(props) => props.theme.base.typography.h5.fontSize};
    font-weight: ${(props) => props.theme.base.typography.h5.fontWeight};
    color: ${(props) => props.theme.colors.dark};
    line-height: ${(props) => props.theme.base.typography.h5.lineHeight};
  }

  a {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: ${(props) => props.theme.base.typography.a.textDecoration};
    cursor: ${(props) => props.theme.base.typography.a.cursor};
    line-height: ${(props) => props.theme.base.typography.a.lineHeight};

    &:hover {
      color: ${(props) => props.theme.colors.primaryHover};
    }
  }

  /* Line-height utility classes */
  .line-height-xs { line-height: ${(props) => props.theme.fonts.lineHeights.xs}; }
  .line-height-sm { line-height: ${(props) => props.theme.fonts.lineHeights.sm}; }
  .line-height-md { line-height: ${(props) => props.theme.fonts.lineHeights.md}; }
  .line-height-lg { line-height: ${(props) => props.theme.fonts.lineHeights.lg}; }
  .line-height-xl { line-height: ${(props) => props.theme.fonts.lineHeights.xl}; }
  .line-height-xxl { line-height: ${(props) => props.theme.fonts.lineHeights.xxl}; }
  .line-height-xxxl { line-height: ${(props) => props.theme.fonts.lineHeights.xxxl}; }

  /* Buttons */
  .btn {
    border-radius: ${(props) => props.theme.buttons.base.borderRadius};
    font-weight: ${(props) => props.theme.buttons.base.fontWeight};
    padding: ${(props) => props.theme.buttons.base.padding};
    text-decoration: ${(props) => props.theme.buttons.base.textDecoration};
    text-transform: ${(props) => props.theme.buttons.base.textTransform};
    ${max.sm} {
      font-size: ${(props) => props.theme.buttons.base['${max.sm}'].fontSize};
    }
    &.disabled {
      cursor: ${(props) => props.theme.buttons.base['&.disabled'].cursor};
    }
    &.btn.focus, &.btn:focus {
      box-shadow: ${(props) => props.theme.buttons.base['&.btn.focus, &.btn:focus'].boxShadow};
    }
  }

  .btn-primary {
    background-color: ${(props) => props.theme.buttons.primary.backgroundColor};
    border-color: ${(props) => props.theme.buttons.primary.borderColor};
    margin: ${(props) => props.theme.buttons.primary.margin};
    text-transform: ${(props) => props.theme.buttons.primary.textTransform};
    &:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active {
      background-color: ${(props) => props.theme.buttons.primary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active'].backgroundColor};
      border-color: ${(props) => props.theme.buttons.primary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active'].borderColor};
    }
    &.btn-primary.disabled, &.btn-primary:disabled {
      background-color: ${(props) => props.theme.buttons.primary['&.btn-primary.disabled, &.btn-primary:disabled'].backgroundColor};
      border-color: ${(props) => props.theme.buttons.primary['&.btn-primary.disabled, &.btn-primary:disabled'].borderColor};
      opacity: ${(props) => props.theme.buttons.primary['&.btn-primary.disabled, &.btn-primary:disabled'].opacity};
    }
  }

  .btn-outline-primary {
    border-color: ${(props) => props.theme.buttons.outlinePrimary.borderColor};
    color: ${(props) => props.theme.buttons.outlinePrimary.color};
    text-transform: ${(props) => props.theme.buttons.outlinePrimary.textTransform};
    &:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active {
      border-color: ${(props) => props.theme.buttons.outlinePrimary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active'].borderColor};
      background-color: ${(props) => props.theme.buttons.outlinePrimary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active'].backgroundColor};
      color: ${(props) => props.theme.buttons.outlinePrimary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active'].color};
    }
    &.btn-primary.disabled, &.btn-primary:disabled {
      border-color: ${(props) => props.theme.buttons.outlinePrimary['&.btn-primary.disabled, &.btn-primary:disabled'].borderColor};
      background-color: ${(props) => props.theme.buttons.outlinePrimary['&.btn-primary.disabled, &.btn-primary:disabled'].backgroundColor};
      color: ${(props) => props.theme.buttons.outlinePrimary['&.btn-primary.disabled, &.btn-primary:disabled'].color};
      opacity: ${(props) => props.theme.buttons.outlinePrimary['&.btn-primary.disabled, &.btn-primary:disabled'].opacity};
    }
  }

  .btn-secondary {
    background: ${(props) => props.theme.buttons.secondary.background};
    color: ${(props) => props.theme.buttons.secondary.color};
    border: ${(props) => props.theme.buttons.secondary.border};
    text-transform: ${(props) => props.theme.buttons.secondary.textTransform};
    &:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled {
      background: ${(props) => props.theme.buttons.secondary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].background};
      border-color: ${(props) => props.theme.buttons.secondary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].borderColor};
      color: ${(props) => props.theme.buttons.secondary['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].color};
    }
  }
  
  .btn-light {
    background: ${(props) => props.theme.buttons.light.background};
    color: ${(props) => props.theme.buttons.light.color};
    text-decoration: ${(props) => props.theme.buttons.light.textDecoration};
    border: ${(props) => props.theme.buttons.light.border};
    text-transform: ${(props) => props.theme.buttons.light.textTransform};
    &:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled {
      background: ${(props) => props.theme.buttons.light['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].background};
      border: ${(props) => props.theme.buttons.light['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].border};
      color: ${(props) => props.theme.buttons.light['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].color};
    }
  }
  
  .btn-link {
    background: ${(props) => props.theme.buttons.link.background};
    border-radius: ${(props) => props.theme.buttons.link.borderRadius};
    font-weight: ${(props) => props.theme.buttons.link.fontWeight};
    text-decoration: ${(props) => props.theme.buttons.link.textDecoration};
    color: ${(props) => props.theme.buttons.link.color};
    text-transform: ${(props) => props.theme.buttons.link.textTransform};
    &:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled {
      text-decoration: ${(props) => props.theme.buttons.link.textDecoration} !important;
      color: ${(props) => props.theme.buttons.link['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].color} !important;
    }
  }
  
  .btn-inline-link {
    background: ${(props) => props.theme.buttons.inlineLink.background};
    border-radius: ${(props) => props.theme.buttons.inlineLink.borderRadius};
    font-weight: ${(props) => props.theme.buttons.inlineLink.fontWeight};
    text-decoration: ${(props) => props.theme.buttons.inlineLink.textDecoration};
    text-transform: ${(props) => props.theme.buttons.inlineLink.textTransform};
    color: ${(props) => props.theme.buttons.inlineLink.color};
    padding: ${(props) => props.theme.buttons.inlineLink.padding};
    vertical-align: ${(props) => props.theme.buttons.inlineLink.verticalAlign};
    &:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled {
      color: ${(props) => props.theme.buttons.inlineLink['&:hover, &:active, &:focus, &:not(:disabled):not(.disabled):active, .btn-primary.disabled, .btn-primary:disabled'].color};
    }
  }

  /* Alert styles */
  .alert {
    border: ${(props) => props.theme.alert.alert.border};
    font-weight: ${(props) => props.theme.alert.alert.fontWeight};
    box-shadow: ${(props) => props.theme.alert.alert.boxShadow};
    
    &.alert-primary {
      color: ${(props) => props.theme.alert.alert['&.alert-primary'].color};
      background-image: ${(props) => props.theme.alert.alert['&.alert-primary'].backgroundImage};
    }
    &.alert-secondary {
      color: ${(props) => props.theme.alert.alert['&.alert-secondary'].color};
      background-image: ${(props) => props.theme.alert.alert['&.alert-secondary'].backgroundImage};
    }
    &.alert-success {
      color: ${(props) => props.theme.alert.alert['&.alert-success'].color};
      background-image: ${(props) => props.theme.alert.alert['&.alert-success'].backgroundImage};
    }
    &.alert-danger {
      color: ${(props) => props.theme.alert.alert['&.alert-danger'].color};
      background-image: ${(props) => props.theme.alert.alert['&.alert-danger'].backgroundImage};
    }
    &.alert-warning {
      color: ${(props) => props.theme.alert.alert['&.alert-warning'].color};
      background-image: ${(props) => props.theme.alert.alert['&.alert-warning'].backgroundImage};
    }
    &.alert-info {
      color: ${(props) => props.theme.alert.alert['&.alert-info'].color};
      background-image: ${(props) => props.theme.alert.alert['&.alert-info'].backgroundImage};
    }
    &.alert-light {
      color: ${(props) => props.theme.alert.alert['&.alert-light'].color};
      background: ${(props) => props.theme.alert.alert['&.alert-light'].background};
    }
    &.alert-dark {
      color: ${(props) => props.theme.alert.alert['&.alert-dark'].color};
      background: ${(props) => props.theme.alert.alert['&.alert-dark'].background};
    }
    
    .close, .btn-close {
      font-weight: ${(props) => props.theme.alert.alert['.close'].fontWeight};
      opacity: ${(props) => props.theme.alert.alert['.close'].opacity};
      font-size: ${(props) => props.theme.alert.alert['.close'].fontSize};
      line-height: ${(props) => props.theme.alert.alert['.close'].lineHeight};
      text-shadow: ${(props) => props.theme.alert.alert['.close'].textShadow};
    }
  }
  
  /* Background variant */
  .bg-dark {
    background-color: ${(props) => props.theme.backgroundVariant.bgDark.backgroundColor};
  }
  
  /* Card styles */
  .card {
    border-radius: ${(props) => props.theme.cards.card.borderRadius};

    ${max.md} {
      padding: ${(props) => props.theme.cards.card['${max.md}'].padding};
    }

    ${max.sm} {
      padding: ${(props) => props.theme.cards.card['${max.sm}'].padding};
    }
  }

  .app-card {
    background: ${(props) => props.theme.cards.appCard.background};
    max-width: ${(props) => props.theme.cards.appCard.maxWidth};
    padding: ${(props) => props.theme.cards.appCard.paddingPhone};
    border-radius: ${(props) => props.theme.cards.appCard.borderRadius};
    box-shadow: ${(props) => props.theme.cards.appCard.boxShadow};
    border: ${(props) => props.theme.cards.appCard.border};

    ${min.sm} {
      padding: ${(props) => props.theme.cards.appCard.paddingDesktop};
    }
  }

  .standard-layout .card {
    border-radius: ${(props) => props.theme.cards.standardLayoutCard.borderRadius};
    .card-body {
      padding: ${(props) => props.theme.cards.standardLayoutCard['.card-body'].padding};
    }
  }

  .onboarding {
    .card {
      box-shadow: ${(props) => props.theme.cards.onboardingCard.boxShadow};

      .onboard-logo {
        display : ${(props) => props.theme.cards.onboardingCard.logo.display};

        ${min.sm} {
          display: ${(props) => props.theme.cards.onboardingCard.logo['${min.sm}'].display};
        }

        ${min.lg} {
          display: ${(props) => props.theme.cards.onboardingCard.logo['${min.lg}'].display};
        }

      }

    }
  }
  
  /* Form styles */
  .form-label {
    font-size: ${(props) => props.theme.forms.formLabel.fontSize};
    font-weight: ${(props) => props.theme.forms.formLabel.fontWeight};
    color: ${(props) => props.theme.forms.formLabel.color};
    margin-bottom: ${(props) => props.theme.forms.formLabel.marginBottom};

    ${min.sm} {
      font-size: ${(props) => props.theme.forms.formLabel['${min.sm}'].fontSize};
    }
  }
  
  .form-control {
    border: ${(props) => props.theme.forms.formControl.border};
    font-size: ${(props) => props.theme.forms.formControl.fontSize};

    ${min.sm} {
      font-size: ${(props) => props.theme.forms.formControl['${min.sm}'].fontSize};
    }

    &:disabled {
      cursor: ${(props) => props.theme.forms.formControl['&:disabled'].cursor};
    }
    &.error {
      border-color: ${(props) => props.theme.forms.formControl['&.error'].borderColor};
    }
    &.is-valid {
      border-color: ${(props) => props.theme.forms.formControl['&.is-valid'].borderColor};
      background-image: ${(props) => props.theme.forms.formControl['&.is-valid'].backgroundImage};
    }
  }


  /* Use svg arrow as background for select dropdown vs default build in one from browser (so we can adjust its position)*/
  select.form-control {
    background-position: ${(props) => props.theme.forms.selectFormControl.backgroundPosition};
    text-overflow: ${(props) => props.theme.forms.selectFormControl.textOverflow};
    word-wrap: ${(props) => props.theme.forms.selectFormControl.wordWrap};
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image:
      url("data:image/svg+xml,%3Csvg viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='currentColor' stroke-width='1' fill='none'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: 12px 8px;
    padding-right: 2.5rem;
  }

  select.form-control::-ms-expand { display: none; } /* old IE/Edge */

  .tab-button {
    border-radius: ${(props) => props.theme.forms.tabButton.borderRadius};
    border-width: ${(props) => props.theme.forms.tabButton.selectedBorderWidth};
  }
  
  /* Modal styles */
  .modal {
    &.welcome {
      ${max.lg} {
        .modal-content {
          background: ${(props) => props.theme.modal.modal['&.welcome']['${max.lg}']['.modal-content'].background};
        }
      }
      ${min.lg} {
        .modal-lg, .modal-xl {
          max-width: ${(props) => props.theme.modal.modal['&.welcome']['${min.lg}']['.modal-lg, .modal-xl'].maxWidth};
        }
      }
    }
    &.fade {
      .modal-dialog {
        -webkit-transform: ${(props) => props.theme.modal.modal['&.fade']['.modal-dialog'].WebkitTransform};
        transform: ${(props) => props.theme.modal.modal['&.fade']['.modal-dialog'].transform};
      }
    }
    .modal-dialog {
      .modal-content {
        border-radius: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content'].borderRadius};
        border: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content'].border};

        .modal-header {
          border-bottom: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header'].borderBottom};

          .close, .btn-close {
            font-size: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].fontSize};
            font-weight: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].fontWeight};
            background: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].background};
            text-shadow: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].textShadow};
            padding: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].padding};
            border-radius: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].borderRadius};
            margin-top: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].marginTop};
            margin-right: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].marginRight};
            color: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].color};
            line-height: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].lineHeight};

            span {
              opacity: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].span.opacity};
            }

            &.btn-close {
              opacity: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-header']['.close'].span.opacity};
            }
          }
        }
        .modal-body {
          .btn-primary {
            margin: ${(props) => props.theme.modal.modal['.modal-dialog']['.modal-content']['.modal-body']['.btn-primary'].margin};
          }
        }
      }
    }
  }
  
  /* Navbar styles */
  .navbar {
    padding: ${(props) => props.theme.navbar.navbar.padding};
    
    ${min.sm} {
      padding: ${(props) => props.theme.navbar.navbar['${min.sm}'].padding};
    }

    ${min.md} {
      padding: ${(props) => props.theme.navbar.navbar['${min.md}'].padding};
    }

    .nav-link {
      text-decoration: ${(props) => props.theme.navbar.navbar['.nav-link'].textDecoration};
      color: ${(props) => props.theme.navbar.navbar['.nav-link'].color};
      font-weight: ${(props) => props.theme.navbar.navbar['.nav-link'].fontWeight};
      font-size: ${(props) => props.theme.navbar.navbar['.nav-link'].fontSize};

      &.active,
      &:hover {
        color: ${(props) => props.theme.navbar.navbar['.nav-link']['&.active, &:hover'].color};
      }
    }

    .navbar-toggler {
      .icon-bar {
        background-color: ${(props) => props.theme.navbar.navbar['.navbar-toggler .icon-bar'].background};
      }
    }

    .onboarding-nav-container & .nav-link:hover {
      opacity: ${(props) => props.theme.navbar.navbar['.onboarding-nav-container & .nav-link:hover'].opacity};
    }

    .theme-logo {
        flex: ${(props) => props.theme.navbar.navbar['.theme-logo'].flex};
        justify-content: ${(props) => props.theme.navbar.navbar['.theme-logo']['justify-content']};
        display: ${(props) => props.theme.navbar.navbar['.theme-logo'].display};
        padding-left:  ${(props) => props.theme.navbar.navbar['.theme-logo'].paddingLeft};

       ${min.sm} {
        flex: ${(props) => props.theme.navbar.navbar['.theme-logo']['${min.sm}'].flex};
        justify-content: ${(props) => props.theme.navbar.navbar['.theme-logo']['${min.sm}']['justify-content']};
        display: ${(props) => props.theme.navbar.navbar['.theme-logo']['${min.sm}'].display};
        padding-left: ${(props) => props.theme.navbar.navbar['.theme-logo']['${min.sm}'].paddingLeft};
      }

      ${min.lg} {
        display: ${(props) => props.theme.navbar.navbar['.theme-logo']['${min.lg}'].display};
        padding-left: ${(props) => props.theme.navbar.navbar['.theme-logo']['${min.lg}'].paddingLeft};
      }
    }
  }

  .onboarding-nav-container {
    .navbar {
      ${min.lg} {
        background: ${(props) => props.theme.navbar.onboarding['${min.lg}'].background};
        border-bottom: ${(props) => props.theme.navbar.onboarding['${min.lg}'].borderBottom || 'none'};
      }
    }
    .theme-logo {
      ${min.lg} {
        display:  ${(props) => props.theme.navbar.onboarding['.theme-logo']['${min.lg}'].display};
      }
      ${min.md} {
        display:  ${(props) => props.theme.navbar.onboarding['.theme-logo']['${min.md}'].display};
      }
      ${min.sm} {
        display:  ${(props) => props.theme.navbar.onboarding['.theme-logo']['${min.sm}'].display};
      }
    }
     
  }
  
  /* Standard layout styles */

  .main-content-column {
    ${min.lg} {
      padding-left: ${(props) => props.theme.standardLayout.mainContentColumn.paddingLeft};
    }
  }

  #portal {
    z-index: ${(props) => props.theme.standardLayout.portal.zIndex};
    bottom: ${(props) => props.theme.standardLayout.portal.bottom};
    left: ${(props) => props.theme.standardLayout.portal.left};
    right: ${(props) => props.theme.standardLayout.portal.right};
    position: ${(props) => props.theme.standardLayout.portal.position};
  }
  
  .app-cta-space {
    height: ${(props) => props.theme.standardLayout.appCtaSpace.height};
    color: ${(props) => props.theme.standardLayout.appCtaSpace.color};
    font-size: ${(props) => props.theme.standardLayout.appCtaSpace.fontSize};
    font-weight: ${(props) => props.theme.standardLayout.appCtaSpace.fontWeight};
  }
  
  ${max.md} {
    .app-cta-space {
      height: ${(props) => props.theme.standardLayout.mediaQueries['${max.md}'].appCtaSpace.height};
    }
    .appstore-footer {
      .col {
        font-size: ${(props) => props.theme.standardLayout.mediaQueries['${max.md}'].appstoreFooter['.col'].fontSize};
      }
      img {
        width: ${(props) => props.theme.standardLayout.mediaQueries['${max.md}'].appstoreFooter['img'].width};
        max-width: ${(props) => props.theme.standardLayout.mediaQueries['${max.md}'].appstoreFooter['img'].maxWidth};
      }
    }
  }
  
  ${max.sm} {
    .app-cta-space {
      height: ${(props) => props.theme.standardLayout.mediaQueries['${max.sm}'].appCtaSpace.height};
    }
    .appstore-footer {
      .col {
        font-size: ${(props) => props.theme.standardLayout.mediaQueries['${max.sm}'].appstoreFooter['.col'].fontSize};
      }
    }
  }
  
  .navbar-toggle {
    border: ${(props) => props.theme.standardLayout.navbarToggle.border};
    background: ${(props) => props.theme.standardLayout.navbarToggle.background};
    &:hover {
      background: ${(props) => props.theme.standardLayout.navbarToggle['&:hover'].background};
    }
    .icon-bar {
      width: ${(props) => props.theme.standardLayout.navbarToggle['.icon-bar'].width};
      transition: ${(props) => props.theme.standardLayout.navbarToggle['.icon-bar'].transition};
      display: ${(props) => props.theme.standardLayout.navbarToggle['.icon-bar'].display};
      height: ${(props) => props.theme.standardLayout.navbarToggle['.icon-bar'].height};
      border-radius: ${(props) => props.theme.standardLayout.navbarToggle['.icon-bar'].borderRadius};
      background-color: ${(props) => props.theme.standardLayout.navbarToggle['.icon-bar'].backgroundColor};
    }
    .top-bar {
      transform: ${(props) => props.theme.standardLayout.navbarToggle['.top-bar'].transform};
      transform-origin: ${(props) => props.theme.standardLayout.navbarToggle['.top-bar'].transformOrigin};
    }
    .middle-bar {
      opacity: ${(props) => props.theme.standardLayout.navbarToggle['.middle-bar'].opacity};
      margin-top: ${(props) => props.theme.standardLayout.navbarToggle['.middle-bar'].marginTop};
    }
    .bottom-bar {
      transform: ${(props) => props.theme.standardLayout.navbarToggle['.bottom-bar'].transform};
      transform-origin: ${(props) => props.theme.standardLayout.navbarToggle['.bottom-bar'].transformOrigin};
      margin-top: ${(props) => props.theme.standardLayout.navbarToggle['.bottom-bar'].marginTop};
    }
  }
  
  .collapsed {
    .top-bar {
      transform: ${(props) => props.theme.standardLayout.collapsed['.top-bar'].transform};
    }
    .middle-bar {
      opacity: ${(props) => props.theme.standardLayout.collapsed['.middle-bar'].opacity};
    }
    .bottom-bar {
      transform: ${(props) => props.theme.standardLayout.collapsed['.bottom-bar'].transform};
    }
  }

   /* Error label styles */
  .error-label {
    font-size: ${(props) => props.theme.errorLabel.errorLabel.fontSize};
    color: ${(props) => props.theme.errorLabel.errorLabel.color};
  }
  
  /* Icon button styles */
  .icon-button {
    text-align: ${(props) => props.theme.iconButton.iconButton.textAlign};
    padding: ${(props) => props.theme.iconButton.iconButton.padding};
    border-radius: ${(props) => props.theme.iconButton.iconButton.borderRadius};
    border: ${(props) => props.theme.iconButton.iconButton.border};
    background: ${(props) => props.theme.iconButton.iconButton.background};
    color: ${(props) => props.theme.iconButton.iconButton.color};
    width: ${(props) => props.theme.iconButton.iconButton.width};
    margin-bottom: ${(props) => props.theme.iconButton.iconButton.marginBottom};
    text-decoration: ${(props) => props.theme.iconButton.iconButton.textDecoration};
    font-weight: ${(props) => props.theme.iconButton.iconButton.fontWeight};
    text-wrap: ${(props) => props.theme.iconButton.iconButton.textWrap};

    .svgHolder {
        width: ${(props) => props.theme.iconButton.iconButton.svgHolder.width};
        display: flex;
        justify-content: center;
    }
    
    &:hover, &.active {
      background: ${(props) => props.theme.iconButton.iconButton['&:hover, &.active'].background};
      border-color: ${(props) => props.theme.iconButton.iconButton['&:hover, &.active'].borderColor};
      color: ${(props) => props.theme.iconButton.iconButton['&:hover, &.active'].color};
      
      svg {
        path {
          fill: ${(props) => props.theme.iconButton.iconButton['&:hover, &.active'].svg.path.fill};
          stroke: ${(props) => props.theme.iconButton.iconButton['&:hover, &.active'].svg.path.stroke || 'none'};
        }
        use {
          fill: ${(props) => props.theme.iconButton.iconButton['&:hover, &.active'].svg.use.fill};
        }
      }
    }
    
    &.active {
      border-width: ${(props) => props.theme.iconButton.iconButton['&.active'].borderWidth};
    }
  }

  /*SVG Icons */

  .svg-icon {
      width: ${(props) => props.theme.icons.styles.svgIcon.width};
      height: ${(props) => props.theme.icons.styles.svgIcon.height};
      border-radius: ${(props) => props.theme.icons.styles.svgIcon.borderRadius};
      display: ${(props) => props.theme.icons.styles.svgIcon.display};
      justify-content: ${(props) => props.theme.icons.styles.svgIcon.justifyContent};
      align-items: ${(props) => props.theme.icons.styles.svgIcon.alignItems};
      margin-bottom: ${(props) => props.theme.icons.styles.svgIcon.marginBottom};
      margin-right: ${(props) => props.theme.icons.styles.svgIcon.marginRight};
      margin-left: ${(props) => props.theme.icons.styles.svgIcon.marginLeft};

      ${min.sm} {
        width: ${(props) => props.theme.icons.styles.svgIcon['${min.sm}'].width};
        height: ${(props) => props.theme.icons.styles.svgIcon['${min.sm}'].height};
        margin-bottom: ${(props) => props.theme.icons.styles.svgIcon['${min.sm}'].marginBottom};
        margin-right: ${(props) => props.theme.icons.styles.svgIcon['${min.sm}'].marginRight};
      }
  }

  .grid-icon {
      border: ${(props) => props.theme.icons.styles.gridIcon.border};
      background: ${(props) => props.theme.icons.styles.gridIcon.background};

      img {
        width: ${(props) => props.theme.icons.styles.gridIcon.img.width};

        ${min.sm} {
            width: ${(props) => props.theme.icons.styles.gridIcon.img['${min.sm}'].width};
          }
       }
  }

  .consumption-icon {
      background: ${(props) => props.theme.icons.styles.consumptionIcon.background};

      img {
         width: ${(props) => props.theme.icons.styles.consumptionIcon.img.width};

          ${min.sm} {
            width: ${(props) => props.theme.icons.styles.consumptionIcon.img['${min.sm}'].width};
          }
       }
  }

  .solar-icon {
      background: ${(props) => props.theme.icons.styles.solarIcon.background};

      img {
        width: ${(props) => props.theme.icons.styles.solarIcon.img.width};

        ${min.sm} {
          width: ${(props) => props.theme.icons.styles.solarIcon.img['${min.sm}'].width};
        }
       }
  }

  .battery-icon {
      background: ${(props) => props.theme.icons.styles.batteryIcon.background};

      img {
        width: ${(props) => props.theme.icons.styles.batteryIcon.img.width};

        ${min.sm} {
          width: ${(props) => props.theme.icons.styles.batteryIcon.img['${min.sm}'].width};
        }
       }
  }

  /* Auth styles */
  div[data-test='authenticator-error'] {
    position: ${(props) => props.theme.auth.authenticatorError.position};
    z-index: ${(props) => props.theme.auth.authenticatorError.zIndex};
    width: ${(props) => props.theme.auth.authenticatorError.width};
    padding: ${(props) => props.theme.auth.authenticatorError.padding};
    color: ${(props) => props.theme.auth.authenticatorError.color};
    background-image: ${(props) => props.theme.auth.authenticatorError.backgroundImage};
    font-weight: ${(props) => props.theme.auth.authenticatorError.fontWeight};
    border-radius: ${(props) => props.theme.auth.authenticatorError.borderRadius};
    box-shadow: ${(props) => props.theme.auth.authenticatorError.boxShadow};
  }
  
  .login-background {
    min-height: ${(props) => props.theme.auth.loginBackground.minHeight};
    min-width: ${(props) => props.theme.auth.loginBackground.minWidth};
    
    .card {
      max-width: ${(props) => props.theme.auth.card.maxWidth};
      width: ${(props) => props.theme.auth.card.width};
      margin-left: auto;
      margin-right: auto;
      
      a {
        text-decoration: ${(props) => props.theme.auth.card.a.textDecoration};
        &:hover {
          color: ${(props) => props.theme.auth.card.a['&:hover'].color};
        }
      }
      
      nav a {
        text-decoration: ${(props) => props.theme.auth.card['nav a'].textDecoration};
      }
      
      hr {
        border-top-color: ${(props) => props.theme.auth.card.hr.borderTopColor};
      }
      
      .signup-text {
        font-weight: ${(props) => props.theme.auth.card.signupText.fontWeight};
        
        ${max.sm} {
          font-size: ${(props) => props.theme.auth.card.signupText['${max.sm}'].fontSize};
        }
      }
      
      .terms {
        font-size: ${(props) => props.theme.auth.card.terms.fontSize};
        
        p {
          font-size: ${(props) => props.theme.auth.card.terms.p.fontSize};
          line-height: ${(props) => props.theme.auth.card.terms.p.lineHeight};
        }
        
        a {
          text-decoration: ${(props) => props.theme.auth.card.terms.a.textDecoration};
          line-height: ${(props) => props.theme.auth.card.terms.a.lineHeight};
          &:hover {
            text-decoration: ${(props) => props.theme.auth.card.terms.a['&:hover'].textDecoration};
          }
        }
      }
      
      .not-found-text {
        font-size: ${(props) => props.theme.auth.card.notFoundText.fontSize};
        a {
          text-decoration: ${(props) => props.theme.auth.card.notFoundText.a.textDecoration};
          &:hover {
            text-decoration: ${(props) => props.theme.auth.card.notFoundText.a['&:hover'].textDecoration};
          }
        }
      }
      
      .verification-input-container {
        display: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacters.display};
        width: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacters.width};
        height: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacters.height};
        grid-template-columns: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacters.gridTemplateColumns};
        grid-gap: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacters.gridGap};
        
        .verification-input-character {
          line-height: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter.lineHeight};
          font-size: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter.fontSize};
          border: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter.border};
          color: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter.color};
          text-align: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter.textAlign};
          border-radius: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter.borderRadius};
          
          &.verification-input-character--inactive {
            color: ${(props) => props.theme.auth.card.verificationInputContainer.verificationInputCharacter['&.verification-input-character--inactive'].color};
          }
        }
        
        .verification-input-character--inactive {
          background-color: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--inactive'].backgroundColor};
          box-shadow: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--inactive'].boxShadow};
        }
        
        .verification-input-character--selected {
          border-color: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected'].borderColor};
          outline: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected'].outline};
          background-color: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected'].backgroundColor};
          
          &::before {
            color: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['&::before'].color};
            content: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['&::before'].content};
            animation-name: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['&::before'].animationName};
            animation-duration: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['&::before'].animationDuration};
            animation-iteration-count: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['&::before'].animationIterationCount};
            opacity: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['&::before'].opacity};
          }
          
          @keyframes blink {
            from {
              opacity: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['@keyframes blink'].from.opacity};
            }
            to {
              opacity: ${(props) => props.theme.auth.card.verificationInputContainer['verification-input-character--selected']['@keyframes blink'].to.opacity};
            }
          }
        }
      }
      
      .meter-code-wrapper {
        margin-bottom: ${(props) => props.theme.auth.card.meterCodeWrapper.marginBottom};
        .verification-input-container {
          grid-template-columns: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacters.gridTemplateColumns};
          grid-gap: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacters.gridGap};
          height: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacters.height};
          .verification-input-character {
            display: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacter.display};
            align-items: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacter.alignItems};
            justify-content: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacter.justifyContent};
            margin-left: ${(props) => props.theme.auth.card.meterCodeWrapper.verificationInputContainer.verificationInputCharacter.marginLeft};
          }
        }
      }
    }
  }
  
  ${max.xs} {
    .verification-input-container {
      height: ${(props) => props.theme.auth.mediaQueries['${max.xs}'].verificationInputCharacters.height};
      .verification-input-character {
        line-height: ${(props) => props.theme.auth.mediaQueries['${max.xs}'].verificationInputCharacters.verificationInputCharacter.lineHeight};
        font-size: ${(props) => props.theme.auth.mediaQueries['${max.xs}'].verificationInputCharacters.verificationInputCharacter.fontSize};
      }
    }
  }
  
  .or-panel {
    display: ${(props) => props.theme.auth.orPanel.display};
    align-items: ${(props) => props.theme.auth.orPanel.alignItems};
    justify-content: ${(props) => props.theme.auth.orPanel.justifyContent};
  
    .or-text {
      font-size: ${(props) => props.theme.auth.orPanel.orText.fontSize};
      margin: ${(props) => props.theme.auth.orPanel.orText.margin};
  
      ${min.sm} {
        font-size: ${(props) => props.theme.auth.orPanel.orText['${min.sm}'].fontSize};
      }
    }
  
    hr {
      width: ${(props) => props.theme.auth.orPanel.hr.width};
      border-top: ${(props) => props.theme.auth.orPanel.hr.borderTop};
    }
  }
`;

// TODO: Remove this ANY type once we have a proper type for this
// Type assertion for GlobalStyle
const TypedGlobalStyle = GlobalStyle as any;

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider component that loads the appropriate theme based on the configuration
 *
 * This is the main component that:
 * 1. Loads the theme configuration using useThemeConfig hook
 * 2. Applies global styles using GlobalStyle
 * 3. Provides the theme to all child components via styled-components' ThemeProvider
 */
const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize with the configured theme or default to Loop

  const [appThemeName, setAppThemeName] = usePersistedState('themeName', themeName);

  const [currentThemeName, setCurrentThemeName] = useState<string>(() => {
    // Try to use the configured theme, fallback to Loop if not available
    try {
      if (appThemeName && availableThemes.some((t) => t.name === appThemeName)) {
        return appThemeName;
      }
    } catch (error) {
      console.warn('Failed to load theme from config:', error);
    }

    return 'Loop';
  });

  // Load the current theme
  const theme = loadTheme(currentThemeName);
  const activeTheme = theme ?? loopTheme;

  // Function to toggle between themes
  const handleThemeToggle = useCallback(() => {
    setCurrentThemeName((prevTheme) => {
      const newTheme = prevTheme === 'Loop' ? 'Utilita' : 'Loop';
      setAppThemeName(newTheme);
      return newTheme;
    });
  }, []);

  return (
    <TypedStyledThemeProvider theme={activeTheme}>
      <>
        {/* Apply global styles based on the active theme */}
        <TypedGlobalStyle />

        {/* Render all child components with access to the theme */}
        {children}

        {/* Floating theme toggle button - only visible when enabled in config */}
        {showThemeToggle && <ThemeToggleButton currentTheme={currentThemeName} onThemeToggle={handleThemeToggle} />}
      </>
    </TypedStyledThemeProvider>
  );
};

export default ThemeProvider;
